import { useState } from 'react';
import useLiterals from 'utils/hooks/useLiterals';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Card from 'components/UI/Card';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import {
  getUserOtp,
  checkLinkOtp,
  validateSession,
  unlinkSAP,
} from 'modules/session/actions';
import OtpModal from 'routes/Auth/components/OtpModal';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import Modal from 'components/Modal';
import { ROUTE_PATH } from 'routes';
import { OTP_TYPES } from 'constants/global';

const SAPAssociate = () => {
  const literals = useLiterals('settings');
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.session);
  const [newClientId, setNewClientId] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const handleSendOtp = () => {
    dispatch(getUserOtp(newClientId, OTP_TYPES.EMAIL))
      .then(() => {
        setShowOtpModal(true);
      }).catch(() => {
        setError({
          title: literals.sap.errorSendOtp,
          description: literals.sap.errorSendOtpDescription,
        });
      });
  };

  const handleVerifyOtp = (otp) => {
    dispatch(checkLinkOtp(newClientId, otp))
      .then(() => {
        setShowOtpModal(false);
        setSuccess({
          title: literals.sap.successTitle,
          description: literals.sap.successDescription,
        });
      }).catch(() => {
        setError({
          title: literals.sap.errorTitle,
          description: literals.sap.errorDescription,
        });
      });
  };

  const handleUnlinkSAP = () => {
    dispatch(unlinkSAP())
      .then(() => {
        setSuccess({
          title: literals.sap.unlinkSuccessTitle,
          description: literals.sap.unlinkSuccessDescription,
        });
      }).catch(() => {
        setError({
          title: literals.sap.unlinkErrorTitle,
          description: literals.sap.unlinkErrorDescription,
        });
      }).finally(() => {
        setShowInfoModal(false);
      });
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
  };

  const handleCloseSuccessModal = () => {
    setSuccess(null);
    dispatch(validateSession());
    navigate(ROUTE_PATH.SETTINGS);
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
  };

  return (
    <>
      {success ? (
        <SuccessModal
          title={success.title}
          description={success.description}
          onCloseNavigateRoute={ROUTE_PATH.SETTINGS}
          onClose={handleCloseSuccessModal}
        >
          <Button
            className='mt-6'
            text={literals.common.accept}
            onClick={handleCloseSuccessModal}
          />
        </SuccessModal>
      ) : null}
      {error ? (
        <ErrorModal
          title={error.title}
          description={error.description || ''}
          onClose={() => setError(null)}
        />
      ) : null}

      <h1 className='mb-2'>{literals.sapAssociate}</h1>
      <p className='mb-6'>{user?.clientId ? literals.isAssociated : literals.isNotAssociated}</p>
      {user?.clientId ? (
        <>
          {showInfoModal && (
            <Modal
              title={literals.sap.unlinkSAP}
              description={literals.sap.infoDescription}
              onClose={handleCloseInfoModal}
            >
              <div className='text-center p-4'>
                <div className='text-neutral-500 mb-6'>{literals.sap.unlinkDescription}</div>
                <div className='flex gap-2 justify-between'>
                  <Button
                    onClick={handleCloseInfoModal}
                    type='secondary'
                    text={literals.common.cancel}
                  />
                  <Button
                    onClick={handleUnlinkSAP}
                    type='primary'
                    text={literals.common.accept}
                  />
                </div>
              </div>
            </Modal>
          )}
          <Card className='w-full'>
            <div className='w-full'>
              <div className='font-bold'>{literals.clientNumber}</div>
              <div className='text-neutral-500 text-sm'>{user.clientId}</div>
              <Button
                className='mt-6 w-full'
                text={literals.sap.unlinkSAP}
                onClick={() => setShowInfoModal(true)}
              />
            </div>
          </Card>
        </>
      ) : (
        <div>
          <OtpModal
            show={showOtpModal}
            otpType={OTP_TYPES.EMAIL}
            onSubmit={handleVerifyOtp}
            onClose={handleCloseOtpModal}
          />
          <Card className='w-full'>
            <Input
              label={literals.inputClientNumber}
              placeholder={literals.clientNumber}
              value={newClientId}
              onChange={setNewClientId}
            />
            <Button
              className='mt-6'
              disabled={!newClientId}
              text={literals.common.save}
              onClick={handleSendOtp}
            />
          </Card>
        </div>
      )}
    </>
  );
};

export default SAPAssociate;
